import { PostComposer } from "./PostComposer.js";

/**
 * Global Post Composer Modal (Singleton)
 * 
 * Provides a system-wide modal for writing posts, specifically solving
 * the issue of "Quoting" content from pages that don't have an inline composer (e.g., MyPage, Search).
 * 
 * Architecture:
 * - Singleton Pattern: Ensures only one modal exists in the DOM.
 * - Dynamic Injection: Injects itself into document.body on first use.
 * - Delegate: Uses existing PostComposer class for the actual logic.
 */
export class PostComposerModal {
    static instance = null;

    constructor() {
        if (PostComposerModal.instance) {
            return PostComposerModal.instance;
        }
        this.overlay = null;
        this.composer = null;
        this.isVisible = false;
        PostComposerModal.instance = this;
    }

    static getInstance() {
        if (!PostComposerModal.instance) {
            PostComposerModal.instance = new PostComposerModal();
        }
        return PostComposerModal.instance;
    }

    /**
     * Show the Global Composer Modal
     * @param {Object} options 
     * @param {Object} options.quoteFeed - Feed object to quote (optional)
     * @param {string} options.initialText - Initial text (optional)
     */
    static show(options = {}) {
        const instance = PostComposerModal.getInstance();
        instance._render(options);
    }

    _render(options) {
        // 1. Create Overlay if not exists
        if (!this.overlay) {
            this.overlay = document.createElement('div');
            this.overlay.className = "fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm opacity-0 transition-opacity duration-300 pointer-events-none"; // Initial hidden state
            this.overlay.innerHTML = `
                <div class="relative w-full max-w-2xl mx-4 bg-transparent scale-95 transition-transform duration-300" id="global-composer-container">
                    <!-- Close Button -->
                    <button class="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors p-2" id="btn-close-global-modal">
                        <span class="material-symbols-outlined text-3xl">close</span>
                    </button>
                    <!-- Composer Target -->
                    <div id="modal-composer-target"></div>
                </div>
            `;
            document.body.appendChild(this.overlay);

            // Bind Close Event
            this.overlay.querySelector('#btn-close-global-modal').onclick = () => this.close();
            this.overlay.onclick = (e) => {
                if (e.target === this.overlay) this.close();
            };
        }

        // 2. Prepare Composer
        const targetContainer = this.overlay.querySelector('#modal-composer-target');

        // Always recreate composer to ensure clean state or reset?
        // PostComposer isn't designed to be re-initialized continuously on same instance easily without 'render()'.
        // Let's instantiate a new one for simplicity and robustness.
        targetContainer.innerHTML = ''; // Clear previous
        this.composer = new PostComposer(targetContainer, () => {
            // On Success
            this.close();
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    icon: 'success',
                    title: '게시 완료',
                    text: '우주에 이야기를 띄워보냈습니다!',
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
        });

        // 3. Apply Options (Quote Mode)
        if (options.quoteFeed) {
            this.composer.activateQuoteMode(options.quoteFeed);
        } else {
            this.composer.render();
        }

        // 4. Show Animation
        // Needed to flush DOM for transition
        requestAnimationFrame(() => {
            this.overlay.classList.remove('opacity-0', 'pointer-events-none');
            this.overlay.querySelector('#global-composer-container').classList.remove('scale-95');
            this.overlay.querySelector('#global-composer-container').classList.add('scale-100');
            this.isVisible = true;

            // Focus Logic
            setTimeout(() => {
                const textarea = targetContainer.querySelector('textarea');
                if (textarea) textarea.focus();
            }, 100);
        });

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    close() {
        if (!this.overlay) return;

        // Hide Animation
        this.overlay.classList.add('opacity-0', 'pointer-events-none');
        this.overlay.querySelector('#global-composer-container').classList.add('scale-95');
        this.overlay.querySelector('#global-composer-container').classList.remove('scale-100');

        setTimeout(() => {
            document.body.style.overflow = '';
            this.isVisible = false;
            // Optional: Destroy internal composer to free memory
        }, 300);
    }
}
